import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useSelector, useDispatch } from "react-redux";
import ZoomableImage from "components/ZoomableImage";
import { Button, Tooltip } from "antd";
import { getAssetData, RECEIVE_ASSET_IDS, receiveAssetData, routeSelected, selectObservation } from "redux/actions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowCircleLeft, faArrowCircleRight } from "@fortawesome/free-solid-svg-icons";
import ObservationModal from "./ObservationModal";
import { GROUND_TRUTH_DISPLAY_NAMES } from "./groundTruthUtils";
import { MEMOIZED_DOMAIN_URL } from "components/util/HostUtils";

const selectedMarkerSelector = (state) => state.markers.selectedMarker;
const assetObservationsSelector = (state) => state.mlAssets.observations;
const assetIDSelector = (state) => state.mlAssets.selected.asset;
const csrfTokenSelector = (state) => state.csrfToken;

const NearbyAssetTab = () => {
    const dispatch = useDispatch();

    const selectedMarker = useSelector(selectedMarkerSelector);
    const assetObservations = useSelector(assetObservationsSelector);
    const assetID = useSelector(assetIDSelector);
    const csrfToken = useSelector(csrfTokenSelector);

    const [selectedObservationIndex, setSelectedObservationIndex] = useState(0);
    const [displayObservationModal, setDisplayObservationModal] = useState(false);

    const markerInfo = {
        "Observation Count": _.get(assetObservations, ["observations", "length"], 0),
    };

    const navigate = (forward) => {
        if (forward) {
            if (assetObservations && selectedObservationIndex < assetObservations.observations.length - 1) {
                setSelectedObservationIndex((idx) => idx + 1);
            }
        } else {
            if (selectedObservationIndex > 0) {
                setSelectedObservationIndex((idx) => idx - 1);
            }
        }
    };

    const goToSession = () => {
        dispatch(routeSelected(selectedObservation.session_id, selectedObservation.timestamp / 1000));
    };

    useEffect(() => {
        setSelectedObservationIndex(0);
    }, [selectedMarker.id]);

    const sortedObservations = useMemo(() => _.orderBy(assetObservations?.observations, "timestamp", "desc"), [assetObservations]);

    const selectedObservation = _.get(sortedObservations, selectedObservationIndex);

    const imageSrc = useMemo(() => {
        if (!selectedObservation) {
            return null;
        }
        let thumbnailImage = selectedObservation.image_url;
        if (thumbnailImage && thumbnailImage.includes(`raw${MEMOIZED_DOMAIN_URL}`) && csrfToken) {
            thumbnailImage += `?csrf=${csrfToken}`;
        }
        return thumbnailImage;
    }, [csrfToken, selectedObservation]);

    if (!_.isEmpty(selectedMarker.ground_truth_data)) {
        Object.keys(selectedMarker.ground_truth_data).forEach((groundTruthKey) => {
            if (GROUND_TRUTH_DISPLAY_NAMES[groundTruthKey]) {
                markerInfo[GROUND_TRUTH_DISPLAY_NAMES[groundTruthKey]] = selectedMarker.ground_truth_data[groundTruthKey];
                markerInfo["Confidence"] = "Ground Truthed";
            }
        });
    } else if (selectedMarker.consensus_data) {
        Object.keys(selectedMarker.consensus_data).forEach((groundTruthKey) => {
            if (GROUND_TRUTH_DISPLAY_NAMES[groundTruthKey]) {
                markerInfo[GROUND_TRUTH_DISPLAY_NAMES[groundTruthKey]] = selectedMarker.consensus_data[groundTruthKey];
            } else if (groundTruthKey === "confidence") {
                markerInfo["Confidence"] = selectedMarker.consensus_data[groundTruthKey];
            }
        });
    }

    const fields = (markerInfo) => {
        return _.map(markerInfo, (value, key) => {
            return (
                <tr
                    key={key}
                    className="Row">
                    <td className={"Key"}>{key}:</td>
                    <td className={"Value " + value}>{value}</td>
                </tr>
            );
        });
    };

    const openObservationModal = () => {
        if (_.has(selectedObservation, "id")) {
            dispatch(getAssetData(assetID)).then((assetData) => {
                const assetCoords = assetData?.coords ?? [];
                const assetGroup = assetData?.issue_group_id;

                const externalMatchCount = assetData?.summary_data?.external_match_count ?? 0;
                const magicMatchCount = assetData?.summary_data?.match_count ?? 0;

                const hasExternal = externalMatchCount > 0;
                const hasMagic = magicMatchCount > 0;

                const observationType = hasExternal && hasMagic ? "magicAndExternal" : hasExternal ? "external" : "magic";

                dispatch({
                    type: RECEIVE_ASSET_IDS,
                    assets: [[assetID, assetCoords, assetGroup, observationType]],
                });
                setDisplayObservationModal(true);
                dispatch(selectObservation(selectedObservation.id));
            });
        }
    };

    return (
        <div className="NearbyAssetTab">
            {displayObservationModal && (
                <ObservationModal
                    isIssue={false}
                    closeModal={() => {
                        setDisplayObservationModal(false);
                        dispatch(receiveAssetData({}));
                        dispatch(selectObservation(null));
                    }}
                    observations={assetObservations.observations}
                />
            )}
            {selectedObservation && (
                <div className="NearbyAssetContainer">
                    <Tooltip title="Previous Observation">
                        <FontAwesomeIcon
                            onClick={() => navigate()}
                            className={"NavigationIcon Left" + (selectedObservationIndex === 0 ? " Disabled" : "")}
                            icon={faArrowCircleLeft}
                        />
                    </Tooltip>

                    <Tooltip title="Next Observation">
                        <FontAwesomeIcon
                            onClick={() => navigate(true)}
                            className={
                                "NavigationIcon Right" +
                                (selectedObservationIndex === _.get(assetObservations, ["observations", "length"], 0) - 1 ? " Disabled" : "")
                            }
                            icon={faArrowCircleRight}
                        />
                    </Tooltip>

                    <div className="NearbyAssetImageContainer">
                        <ZoomableImage
                            zoom={1}
                            imgSrc={imageSrc}
                            destroyOnClose={true}
                            updateZoom={() => {}}
                        />
                    </div>
                </div>
            )}

            <Button
                onClick={goToSession}
                type="primary"
                className="SessionButton"
                disabled={!_.get(assetObservations, ["observations", "length"], 0)}>
                View In Session
            </Button>

            <Button
                onClick={openObservationModal}
                type="primary"
                className="SessionButton"
                disabled={!_.get(assetObservations, ["observations", "length"], 0)}
                style={{ marginTop: "5px" }}>
                View Asset
            </Button>

            <table className={"markerInformationTable"}>
                <tbody>{fields(markerInfo)}</tbody>
            </table>
        </div>
    );
};

export default NearbyAssetTab;
